import { useMemo } from 'react'
import { truncate } from 'lodash-es'
import { observer } from 'mobx-react-lite'
import { FormGroupContainer } from '@/components/FormContainer'
import { FormRow } from '@/components/Form'
import {
  CustomSelect,
  SingleItemSelectedDefault,
} from '@/components/inputs/CustomSelect'
import { ItemSource } from '@/components/inputs/custom-select-state-observable'
import { useCustomSelectStateFromInfiniteScrollProvider } from '@/components/inputs/infinite-scroll-provider-hook'
import { useCustomSelectStateFromItemSource } from '@/components/inputs/item-source-hook'
import { SearchResultItem } from '@/components/inputs/SearchResultItem'
import { Avatar } from '@/components/Avatar'
import {
  CreateOCRScopeJobsDialogManualQuestionsRow,
  CreateOCRScopeJobsDialogSaveAnswersRow,
  CreateOCRScopeJobsDialogScopeJobModeRow,
  CreateOCRScopeJobsDialogScopeNewJobsForm,
} from '@/features/jobs/scoping/components/CreateOCRScopeJobsDialog'
import { type CreateOCRScopeJobsViewModel } from './CreateOCRScopeJobsViewModel'
import { OCRManualQuestionsInputContainer } from './OCRManualQuestionsInputContainer'

export interface NonAdminScopeNewJobsContentContainerProps {
  vm: CreateOCRScopeJobsViewModel
  testId?: string
}

export const NonAdminScopeNewJobsContentContainer = observer(
  function NonAdminScopeNewJobsContentContainer({
    vm,
    testId,
  }: NonAdminScopeNewJobsContentContainerProps) {
    const workspaceSelectProps = useCustomSelectStateFromItemSource({
      itemSource: useMemo(
        () =>
          ItemSource.static(vm.workspaces, (item, text) => {
            const lowerCaseText = text.toLowerCase()
            return item.name.toLowerCase().includes(lowerCaseText)
          }),
        [vm.workspaces],
      ),
    })
    const fileSelectProps = useCustomSelectStateFromItemSource({
      itemSource: useMemo(
        () =>
          ItemSource.static(vm.selectedFiles, (item, text) => {
            const lowerCaseText = text.toLowerCase()
            return item.name.toLowerCase().includes(lowerCaseText)
          }),
        [vm.selectedFiles],
      ),
    })
    const legendSelectProps = useCustomSelectStateFromInfiniteScrollProvider({
      scrollProvider: vm.legendScrollProvider,
      searchResults: vm.filteredLegends,
      searchText: vm.legendSearchText,
      onSearchTextChange: vm.setLegendSearchText,
    })
    const templateSelectProps = useCustomSelectStateFromItemSource({
      itemSource: useMemo(
        () =>
          ItemSource.static(vm.templates ?? [], (item, text) => {
            const lowerCaseText = text.toLowerCase()
            return item.name.toLowerCase().includes(lowerCaseText)
          }),
        [vm.templates],
      ),
    })
    const championClientTeamMembershipsProps =
      useCustomSelectStateFromInfiniteScrollProvider({
        scrollProvider: vm.championClientTeamMembershipsScrollProvider,
        searchResults: vm.filteredChampionClientTeamMemberships,
        searchText: vm.championClientTeamMembershipsSearchText,
        onSearchTextChange: vm.setChampionClientTeamMembershipsSearchText,
      })
    const championClientTeamMembersProps =
      useCustomSelectStateFromInfiniteScrollProvider({
        scrollProvider: vm.championClientTeamMembersScrollProvider,
        searchResults: vm.filteredChampionClientTeamMembers,
        searchText: vm.championClientTeamMembersSearchText,
        onSearchTextChange: vm.setChampionClientTeamMembersSearchText,
      })
    const supportingTeamMembersProps =
      useCustomSelectStateFromInfiniteScrollProvider({
        scrollProvider: vm.championClientTeamMembersScrollProvider,
        searchResults: vm.filteredSupportingTeamMembers,
        searchText: vm.supportingTeamMembersSearchText,
        onSearchTextChange: vm.setSupportingTeamMembersSearchText,
      })

    return (
      <CreateOCRScopeJobsDialogScopeNewJobsForm
        isFetchingManualQuestions={vm.fetchManualInputs.pending}
      >
        <CreateOCRScopeJobsDialogScopeJobModeRow
          showModeSelection={!vm.isOnlyOneFileSelected}
          scopeJobMode={vm.scopeJobMode}
          onChangeScopeJobMode={vm.onChangeScopeJobMode}
        />

        {vm.scopeJobMode === 'Individually' && (
          <FormRow>
            <FormGroupContainer
              label="Uploaded documents"
              help="Selected document will be scoped based on answers below."
              error={vm.validation.getError('selectedFile')}
            >
              <CustomSelect
                {...fileSelectProps}
                icon="dropdown"
                iconColor="var(--grey600)"
                itemToString={(i) => truncate(i.name, { length: 60 })}
                value={vm.selectedFile}
                multi={false}
                onChange={vm.setSelectedFile}
                renderItem={(itemProps) => (
                  <SingleItemSelectedDefault
                    {...itemProps}
                    item={itemProps}
                    itemToString={(i) => truncate(i.item?.name, { length: 60 })}
                  />
                )}
                renderSearchItem={({
                  item,
                  itemToString: _itemToString,
                  ...itemProps
                }) => (
                  <SearchResultItem {...itemProps}>
                    {item.name}
                  </SearchResultItem>
                )}
                testId={testId ? `${testId}-document-select` : undefined}
              />
            </FormGroupContainer>
          </FormRow>
        )}

        {vm.workspaces.length > 1 && (
          <FormRow>
            <FormGroupContainer
              label="Workspace"
              error={vm.validation.getError('workspace')}
            >
              <CustomSelect
                {...workspaceSelectProps}
                placeholder="Select a workspace"
                icon="dropdown"
                iconColor="var(--grey600)"
                itemToString={(v) => String(v.name)}
                value={vm.workspace}
                multi={false}
                onChange={vm.setWorkspace}
                renderItem={(itemProps) => (
                  <SingleItemSelectedDefault
                    {...itemProps}
                    visual={
                      <Avatar
                        size={24}
                        subject={{
                          displayName: itemProps.item.name,
                          avatar: itemProps.item.branding.logos.icon,
                        }}
                      />
                    }
                  />
                )}
                renderSearchItem={({
                  item,
                  itemToString: _itemToString,
                  ...itemProps
                }) => (
                  <SearchResultItem
                    {...itemProps}
                    visual={
                      <Avatar
                        size={24}
                        subject={{
                          avatar: item.branding.logos.icon,
                          displayName: item.name,
                        }}
                      />
                    }
                  >
                    {item.name}
                  </SearchResultItem>
                )}
                testId={testId ? `${testId}-workspace-select` : undefined}
              />
            </FormGroupContainer>
          </FormRow>
        )}

        {vm.canSelectLegend && (
          <FormRow>
            <FormGroupContainer
              label="Legend"
              error={vm.validation.getError('legend')}
            >
              <CustomSelect
                {...legendSelectProps}
                placeholder="Select a legend"
                icon="dropdown"
                iconColor="var(--grey600)"
                itemToString={(v) => String(v.name)}
                value={vm.legend}
                multi={false}
                onChange={vm.setLegend}
                renderItem={(itemProps) => (
                  <SingleItemSelectedDefault
                    {...itemProps}
                    item={itemProps}
                    itemToString={(i) => String(i.item?.name)}
                  />
                )}
                renderSearchItem={({
                  item,
                  itemToString: _itemToString,
                  ...itemProps
                }) => (
                  <SearchResultItem {...itemProps}>
                    {item.name}
                  </SearchResultItem>
                )}
                testId={testId ? `${testId}-legend-select` : undefined}
              />
            </FormGroupContainer>
          </FormRow>
        )}

        {vm.canSelectTemplate && (
          <FormRow>
            <FormGroupContainer
              label="Template"
              error={vm.validation.getError('selectedTemplate')}
            >
              <CustomSelect
                {...templateSelectProps}
                placeholder="Select a template"
                icon="dropdown"
                iconColor="var(--grey600)"
                itemToString={(v) => String(v.name)}
                value={vm.selectedTemplate}
                multi={false}
                onChange={vm.setSelectedTemplate}
                renderItem={(itemProps) => (
                  <SingleItemSelectedDefault
                    {...itemProps}
                    item={itemProps}
                    itemToString={(i) => String(i.item?.name)}
                  />
                )}
                renderSearchItem={({
                  item,
                  itemToString: _itemToString,
                  ...itemProps
                }) => (
                  <SearchResultItem {...itemProps}>
                    {item.name}
                  </SearchResultItem>
                )}
                testId={testId ? `${testId}-template-select` : undefined}
              />
            </FormGroupContainer>
          </FormRow>
        )}

        {vm.canSelectTeamAsNonAdminUser && (
          <FormRow>
            <FormGroupContainer label="Team">
              <CustomSelect
                {...championClientTeamMembershipsProps}
                placeholder="To scope a job for team members, select a team"
                multi={false}
                icon="dropdown"
                iconColor="var(--grey600)"
                itemToString={(i) => truncate(i.name, { length: 60 })}
                value={vm.selectedNonAdminUserTeam}
                onChange={vm.setSelectedNonAdminUserTeam}
                renderSearchItem={({
                  item,
                  itemToString: _itemToString,
                  ...itemProps
                }) => (
                  <SearchResultItem {...itemProps}>
                    {item.name}
                  </SearchResultItem>
                )}
                testId={testId ? `${testId}-team-select` : undefined}
              />
            </FormGroupContainer>
          </FormRow>
        )}

        {vm.workspace && (
          <FormRow>
            <FormGroupContainer
              label="Champion client"
              error={vm.validation.getError('championClient')}
            >
              <CustomSelect
                {...championClientTeamMembersProps}
                placeholder="Select a champion client"
                multi={false}
                icon="dropdown"
                iconColor="var(--grey600)"
                renderItem={(itemProps) => (
                  <SingleItemSelectedDefault
                    {...itemProps}
                    item={itemProps}
                    itemToString={(i) => String(i.item?.displayName)}
                    visual={<Avatar size={24} subject={itemProps.item} />}
                  />
                )}
                value={vm.championClient}
                onChange={vm.setChampionClient}
                renderSearchItem={({
                  item,
                  itemToString: _itemToString,
                  ...itemProps
                }) => (
                  <SearchResultItem
                    {...itemProps}
                    visual={
                      <Avatar
                        size={24}
                        subject={{
                          avatar: item.avatar,
                          displayName: item.displayName,
                        }}
                      />
                    }
                  >
                    {item.displayName}
                  </SearchResultItem>
                )}
                testId={testId ? `${testId}-champion-client-select` : undefined}
              />
            </FormGroupContainer>
          </FormRow>
        )}

        {vm.canSelectSupportingClientAsNonAdminUser && (
          <FormRow>
            <FormGroupContainer label="Supporting client">
              <CustomSelect
                {...supportingTeamMembersProps}
                placeholder="Select a supporting client, if needed"
                multi={false}
                icon="dropdown"
                iconColor="var(--grey600)"
                renderItem={(itemProps) => (
                  <SingleItemSelectedDefault
                    {...itemProps}
                    item={itemProps}
                    itemToString={(i) => String(i.item?.displayName)}
                    visual={<Avatar size={24} subject={itemProps.item} />}
                  />
                )}
                value={vm.supportingClient}
                onChange={vm.setSupportingClient}
                renderSearchItem={({
                  item,
                  itemToString: _itemToString,
                  ...itemProps
                }) => (
                  <SearchResultItem
                    {...itemProps}
                    visual={
                      <Avatar
                        size={24}
                        subject={{
                          avatar: item.avatar,
                          displayName: item.displayName,
                        }}
                      />
                    }
                  >
                    {item.displayName}
                  </SearchResultItem>
                )}
                testId={
                  testId ? `${testId}-supporting-client-select` : undefined
                }
              />
            </FormGroupContainer>
          </FormRow>
        )}
        {vm.canSelectManualQuestions && (
          <CreateOCRScopeJobsDialogManualQuestionsRow>
            {vm.manualQuestions?.map((question, i) => (
              <OCRManualQuestionsInputContainer
                vm={vm}
                question={question}
                testId={testId}
                key={i}
              />
            ))}
          </CreateOCRScopeJobsDialogManualQuestionsRow>
        )}

        {vm.canSaveAnswers && (
          <CreateOCRScopeJobsDialogSaveAnswersRow
            saveAnswers={vm.saveAnswers}
            onToggleSaveAnswers={vm.toggleSaveAnswers}
          />
        )}
      </CreateOCRScopeJobsDialogScopeNewJobsForm>
    )
  },
)
